import React, { useMemo, useState } from "react";
import DashboardModal from "../components/DashboardModal";
import DashboardSelect from "../components/DashboardSelect";
import MaskedDateInput from "../components/MaskedDateInput";
import { KenloPropertyOption } from "../utils/getKenloPropertyOptions";
import { createPropertyValue } from "../services/ownerPatrimonyService";
import { isCompleteMaskedDate, maskedDisplayToIsoDate } from "../utils/maskedDate";
import { parseCurrencyInput } from "../utils/parseCurrencyInput";

interface RegisterPropertyValueModalProps {
  options: KenloPropertyOption[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function RegisterPropertyValueModal({
  options,
  onClose,
  onSuccess,
}: RegisterPropertyValueModalProps) {
  const [kenloPropertyId, setKenloPropertyId] = useState(options[0]?.kenloPropertyId ?? "");
  const [value, setValue] = useState("");
  const [marketValue, setMarketValue] = useState("");
  const [purchaseDateDisplay, setPurchaseDateDisplay] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectOptions = useMemo(
    () =>
      options.map((option) => ({
        value: option.kenloPropertyId,
        label: option.label,
      })),
    [options]
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsedValue = parseCurrencyInput(value);
    const trimmedMarketValue = marketValue.trim();
    const parsedMarketValue =
      trimmedMarketValue.length > 0 ? parseCurrencyInput(trimmedMarketValue) : null;

    if (!kenloPropertyId) {
      setError("Selecione um imóvel.");
      return;
    }

    if (parsedValue == null || parsedValue <= 0) {
      setError("Informe um valor de aquisição válido.");
      return;
    }

    if (trimmedMarketValue.length > 0 && (parsedMarketValue == null || parsedMarketValue <= 0)) {
      setError("Informe um valor de mercado válido ou deixe o campo em branco.");
      return;
    }

    const trimmedPurchaseDate = purchaseDateDisplay.trim();
    const purchaseDate =
      trimmedPurchaseDate.length > 0 ? maskedDisplayToIsoDate(trimmedPurchaseDate) : null;

    if (trimmedPurchaseDate.length > 0 && !purchaseDate) {
      setError("Informe uma data de compra válida no formato dd/mm/aaaa.");
      return;
    }

    setIsSubmitting(true);

    try {
      await createPropertyValue({
        kenlo_property_id: kenloPropertyId,
        value: parsedValue,
        ...(parsedMarketValue != null ? { market_value: parsedMarketValue } : {}),
        ...(purchaseDate ? { purchase_date: purchaseDate } : {}),
      });
      onSuccess();
      onClose();
    } catch {
      setError("Não foi possível cadastrar o valor. Verifique se o imóvel já foi registrado.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardModal title="Cadastrar valor do imóvel" onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {options.length === 0 ? (
          <p className="text-sm text-[#8b949e]">
            Nenhum imóvel disponível para cadastro no momento. Assim que houver repasses
            vinculados à sua conta, eles aparecerão aqui.
          </p>
        ) : (
          <DashboardSelect
            label="Imóvel"
            value={kenloPropertyId}
            options={selectOptions}
            onChange={setKenloPropertyId}
            placeholder="Selecione um imóvel"
          />
        )}

        <label className="flex flex-col gap-1.5 text-sm text-[#8b949e]">
          Valor de aquisição (R$)
          <input
            type="text"
            inputMode="decimal"
            placeholder="215500"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            className="rounded-xl border border-white/[0.08] bg-[#0d1117] px-3 py-2.5 text-sm text-[#f0f6fc] outline-none focus:border-[#6ea8ff]/50"
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm text-[#8b949e]">
          Valor de mercado (R$) — opcional
          <input
            type="text"
            inputMode="decimal"
            placeholder="320000"
            value={marketValue}
            onChange={(event) => setMarketValue(event.target.value)}
            className="rounded-xl border border-white/[0.08] bg-[#0d1117] px-3 py-2.5 text-sm text-[#f0f6fc] outline-none focus:border-[#6ea8ff]/50"
          />
          <span className="text-xs text-[#6e7681]">
            Útil em leilão ou quando o valor atual de mercado for diferente da aquisição. Se
            ficar em branco, o rendimento sobre mercado usa a estimativa FIPE Zap.
          </span>
        </label>

        <label className="flex flex-col gap-1.5 text-sm text-[#8b949e]">
          Data de compra (opcional)
          <MaskedDateInput
            value={purchaseDateDisplay}
            onChange={setPurchaseDateDisplay}
          />
          {purchaseDateDisplay.length > 0 && !isCompleteMaskedDate(purchaseDateDisplay) && (
            <span className="text-xs text-[#6e7681]">Use o formato dd/mm/aaaa</span>
          )}
        </label>

        {error && <p className="text-sm text-[#f85149]">{error}</p>}

        <button
          type="submit"
          disabled={isSubmitting || options.length === 0}
          className="rounded-full bg-[#6ea8ff] px-4 py-2.5 text-sm font-bold text-[#0d1117] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? "Salvando..." : "Salvar valor"}
        </button>
      </form>
    </DashboardModal>
  );
}
